import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react'
import * as _ from 'lodash-es'
import PropTypes from 'prop-types'
import React from 'react'
import { twMerge as mergeClassNames } from 'tailwind-merge'

import { CheckBox } from '@/components/forms/CheckBox'
import { DateRangePicker } from '@/components/forms/DateRangePicker'
import { MultiSelectDropdown } from '@/components/forms/MultiSelectDropdown'
import { FilterIcon } from '@/components/icons'
import { DEFAULT_FILTERS } from '@/pages/DealerDetails/constants'

import { DROPDOWN_FILTERS } from './constants'

const FilterPopover = ({
  filters,
  onFiltersChange,
  checkboxFilters,
  dropdownOptions,
  dropdownFilters,
  dropdownColumns,
  defaultFilters,
  checkboxLabel,
  dateRangeFilters,
  dateValues,
  onDateChange,
  anchor,
  testId,
  ariaLabel,
}) => {
  const resolvedDropdownFilters = dropdownFilters || DROPDOWN_FILTERS
  const resolvedDefaultFilters = defaultFilters || DEFAULT_FILTERS
  const hasActiveDates = _.values(dateValues).some(Boolean)
  const isActive = _.values(filters).some((v) => v.length > 0 || v === true) || hasActiveDates

  // All date-range params cleared — used by Reset.
  const clearedDates = dateRangeFilters.reduce(
    (acc, { from, to }) => ({ ...acc, [from]: '', [to]: '' }),
    {},
  )

  const midpoint = Math.ceil(checkboxFilters.length / 2)
  const leftCheckboxes = checkboxFilters.slice(0, midpoint)
  const rightCheckboxes = checkboxFilters.slice(midpoint)

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Popover className="relative">
        <PopoverButton
          className={mergeClassNames(
            'bg-background border-brownGray-200 hover:bg-charchoal-light hover:border-brownGray-300 rounded-lg border-2 p-3 outline-none ring-0',
            isActive && 'border-blue-800 hover:border-blue-900',
          )}
          data-testid={testId}
          aria-label={ariaLabel}
        >
          <FilterIcon
            className={mergeClassNames('fill-charcoal-400 h-4 w-4', isActive && 'fill-blue-800')}
          />
        </PopoverButton>

        <PopoverPanel
          anchor={anchor}
          className="z-10 flex w-[420px] flex-col gap-2 !overflow-visible rounded-lg bg-[#ffffff] p-3 shadow-md"
        >
          <span className="text-md font-medium">Filters</span>

          {checkboxLabel && (
            <span className="text-charcoal-400 text-xs font-semibold uppercase tracking-widest">
              {checkboxLabel}
            </span>
          )}

          <div className="flex w-full flex-row">
            <div className="flex w-1/2 flex-col gap-1">
              {leftCheckboxes.map(({ key, label, name }) => (
                <CheckBox
                  key={key}
                  label={label}
                  name={name}
                  onChange={() =>
                    onFiltersChange({
                      ...filters,
                      [key]: !filters[key],
                    })
                  }
                  value={filters[key]}
                />
              ))}
            </div>

            <div className="flex w-1/2 flex-col gap-1">
              {rightCheckboxes.map(({ key, label, name }) => (
                <CheckBox
                  key={key}
                  label={label}
                  name={name}
                  onChange={() =>
                    onFiltersChange({
                      ...filters,
                      [key]: !filters[key],
                    })
                  }
                  value={filters[key]}
                />
              ))}
            </div>
          </div>

          <div
            className={mergeClassNames(
              'grid gap-2',
              dropdownColumns === 2 ? 'grid-cols-2' : 'grid-cols-1',
            )}
          >
            {resolvedDropdownFilters.map(({ key, label, id, openAbove, search }) => (
              <MultiSelectDropdown
                key={key}
                className="bg-[#ffffff]"
                id={id}
                label={label}
                listBoxClassName="bg-[#ffffff]"
                onChange={(o) => onFiltersChange({ ...filters, [key]: o })}
                openAbove={openAbove}
                options={dropdownOptions[key] || []}
                search={search}
                value={filters[key]}
              />
            ))}
          </div>

          {dateRangeFilters.length > 0 && (
            <div className="grid grid-cols-1 gap-2">
              {dateRangeFilters.map(({ label, from, to }) => (
                <DateRangePicker
                  key={label}
                  label={label}
                  value={{ from: dateValues[from] || '', to: dateValues[to] || '' }}
                  onChange={(range) =>
                    onDateChange({ ...dateValues, [from]: range.from, [to]: range.to })
                  }
                />
              ))}
            </div>
          )}
        </PopoverPanel>
      </Popover>
      {isActive && (
        <button
          className="cursor-pointer rounded-md text-sm font-semibold leading-5 tracking-[0.25px] text-blue-800 hover:text-blue-900"
          onClick={() => {
            onFiltersChange(resolvedDefaultFilters)
            if (dateRangeFilters.length > 0) onDateChange(clearedDates)
          }}
          type="button"
        >
          Reset Filters
        </button>
      )}
    </div>
  )
}

FilterPopover.defaultProps = {
  anchor: 'bottom end',
  testId: 'filter',
  ariaLabel: 'Filter Repair Orders',
  dropdownOptions: {},
  dropdownFilters: null,
  dropdownColumns: 1,
  defaultFilters: null,
  checkboxLabel: null,
  dateRangeFilters: [],
  dateValues: {},
  onDateChange: () => {},
}

FilterPopover.propTypes = {
  filters: PropTypes.object.isRequired,
  onFiltersChange: PropTypes.func.isRequired,
  checkboxFilters: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }),
  ).isRequired,
  dropdownOptions: PropTypes.object,
  dropdownFilters: PropTypes.array,
  dropdownColumns: PropTypes.oneOf([1, 2]),
  defaultFilters: PropTypes.object,
  checkboxLabel: PropTypes.string,
  dateRangeFilters: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      from: PropTypes.string.isRequired,
      to: PropTypes.string.isRequired,
    }),
  ),
  dateValues: PropTypes.object,
  onDateChange: PropTypes.func,
  anchor: PropTypes.string,
  testId: PropTypes.string,
  ariaLabel: PropTypes.string,
}

export default FilterPopover
